import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import {
    ApiFragment,
    CustomErrorType,
    FragmentId,
    Metadata,
    ShToastFactory,
    ZoneRestrictions,
    ZonesService
} from '@stylehaven/components';
import { MhSelection } from '../../common/entities/user/selection.entity';
import { waitForIndex } from '../../common/services/api/helpers/wait-for-index.helper';
import { RecordsService } from '../../common/services/api/records.service';
import { HttpService } from '../../common/services/http/http.service';
import { UserStoreService } from '../../common/services/user/user-store.service';
import { BaseSelectionService } from '../base-selection.service';

@Injectable({ providedIn: 'root' })
export class PersonalSelectionService extends BaseSelectionService {
    private readonly selectionEndpoint: string = 'v2/users/current/selection';
    private userSelection: MhSelection;

    constructor(
        protected recordsService: RecordsService,
        protected userStoreService: UserStoreService,
        protected zoneRestrictions: ZoneRestrictions,
        protected zoneService: ZonesService,
        protected toastFactory: ShToastFactory,
        protected translateService: TranslateService,
        private readonly http: HttpService
    ) {
        super(recordsService, userStoreService, zoneRestrictions, zoneService, toastFactory, translateService);
        this.initMhSelection();
    }

    private initMhSelection(): void {
        this.userStoreService
            .shouldInitializeSelection()
            .pipe(
                distinctUntilChanged(),
                filter(shouldInitialize => shouldInitialize === true),
                switchMap(() =>
                    this.getPersonalSelection().pipe(
                        map(fragment => {
                            return {
                                fragmentId: fragment.getFragmentId(),
                                relations: fragment.getRelationFragmentIdsOfType('Contains')
                            };
                        })
                    )
                )
            )
            .subscribe(
                userSelection => {
                    this.userStoreService.saveMhSelection(userSelection);
                    this.userSelection = userSelection;
                },
                error => {
                    this.userStoreService.setMhSelectionFailed();
                }
            );
    }

    public getPersonalSelection(): Observable<ApiFragment> {
        const request: string = this.selectionEndpoint;

        return this.http.get<Metadata>(request).pipe(map(result => new ApiFragment(result)));
    }

    public getMhSelection(): Observable<MhSelection> {
        return this.userStoreService.getMhSelection();
    }

    public addFragmentsToSelection(fragments: readonly ApiFragment[]): Observable<ApiFragment> {
        const request: string = this.selectionEndpoint;
        const Records: FragmentId[] = fragments.map(fragment => fragment.getFragmentId());

        return this.http.post<Metadata>(request, { Records }).pipe(
            switchMap(() => this.waitForSelectionUpdate()),
            tap(result => {
                const selection: MhSelection = {
                    fragmentId: result.getFragmentId(),
                    relations: result.getRelationFragmentIdsOfType('Contains')
                };
                this.userSelection = selection;
                return this.userStoreService.saveMhSelection(selection);
            })
        );
    }

    public removeFragmentsFromSelection(fragments: readonly ApiFragment[]): Observable<ApiFragment> {
        const request: string = this.selectionEndpoint;
        const Records: FragmentId[] = fragments.map(fragment => fragment.getFragmentId());

        return this.http.post<Metadata>(request, { RecordsStrategy: 'SUBTRACT', Records }).pipe(
            switchMap(() => this.waitForSelectionUpdate()),
            tap(result => {
                const selection: MhSelection = {
                    fragmentId: result.getFragmentId(),
                    relations: result.getRelationFragmentIdsOfType('Contains')
                };
                this.userSelection = selection;
                return this.userStoreService.saveMhSelection(selection);
            })
        );
    }

    public emptySelection(selectionFragment: ApiFragment): Observable<ApiFragment> {
        const request: string = this.selectionEndpoint;
        const Records: FragmentId[] = selectionFragment.getSelectionItemsIds();

        return this.http.post<Metadata>(request, { RecordsStrategy: 'SUBTRACT', Records }).pipe(
            switchMap(() => this.waitForSelectionUpdate()),
            tap(result => {
                const selection: MhSelection = {
                    fragmentId: result.getFragmentId(),
                    relations: result.getRelationFragmentIdsOfType('Contains')
                };
                this.userSelection = selection;
                return this.userStoreService.saveMhSelection(selection);
            })
        );
    }

    private waitForSelectionUpdate(): Observable<ApiFragment> {
        return this.getPersonalSelection().pipe(
            switchMap(selection => {
                if (this.userSelection.relations.length !== selection.getRelationFragmentIdsOfType('Contains').length) {
                    return of(selection);
                }
                return throwError(CustomErrorType.IndexTimeout);
            }),
            waitForIndex()
        );
    }
}
